import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { Hero } from '../components/home/Hero';
import { IntegrationModel } from '../components/home/IntegrationModel';
import { BusinessAreas } from '../components/home/BusinessAreas';
import { ControlTowerPreview } from '../components/home/ControlTowerPreview';
import { ProofSection } from '../components/home/ProofSection';
import { ModelSteps } from '../components/features/ModelSteps';
import { DocumentPlatform } from '../components/features/DocumentPlatform';
import { Container, Eyebrow, Heading } from '../components/ui/Section';
import { CtaBand } from '../components/ui/CtaBand';
import { Faq } from '../components/ui/Faq';
import { faqJsonLd, localBusinessJsonLd, useSeo } from '../utils/seo';
import { IMAGES, TECH_CAPABILITIES } from '../data/solutions';
const FAQS = [{
  question: '¿Qué servicios puede asumir Rabemar dentro de mi operación?',
  answer: 'Logística y Supply Chain, transporte y distribución, Control Tower, Facility Management (limpieza, mantenimiento técnico, control de plagas, parquización y poda en altura), HSE y seguridad, tecnología y control documental, y sustentabilidad. Se contratan de forma integral o por unidad.'
}, {
  question: '¿Rabemar trabaja con flota propia o con transportistas?',
  answer: 'Con un modelo mixto: 20 unidades de activos fijos más una red 3PL, con más de 150 unidades habilitadas disponibles para distintos tipos de operación en Argentina, bajo gestión integral de transportistas y control documental.'
}, {
  question: '¿Cómo se controla el cumplimiento del servicio?',
  answer: 'A través del Control Tower: seguimiento de transportes, coordinación operativa, control documental, KPIs y medición de SLA. El objetivo operativo del modelo es 90% dentro de las 72 horas, buscando completar operaciones en 48 horas cuando las condiciones lo permiten.'
}, {
  question: '¿Dónde opera Rabemar?',
  answer: 'La base está en Solís 1518, Pergamino, Provincia de Buenos Aires, con capacidad de cobertura en todo el territorio argentino a través de flota propia y red 3PL.'
}, {
  question: '¿Se puede empezar por un solo servicio?',
  answer: 'Sí. El punto de partida habitual es un diagnóstico operativo sobre el área con mayor fricción y, a partir de allí, se amplía el alcance según los resultados medidos.'
}];
export function Home() {
  useSeo({
    title: 'Rabemar Servicios Integrales | Infraestructura operativa para empresas',
    description: 'Logística, Facility Management, HSE, Control Tower, tecnología y sustentabilidad en un único socio operativo. Servicios integrales para empresas en Pergamino y en todo el territorio argentino.',
    path: '/',
    jsonLd: [localBusinessJsonLd, faqJsonLd(FAQS)]
  });
  return <>
      <Hero data-id="element-669" data-mp="2m89k52n3n62q" />
      <IntegrationModel data-id="element-670" data-mp="2r0t02699edap" />
      <BusinessAreas data-id="element-671" data-mp="2r12nbm2x66cp" />
      <ControlTowerPreview data-id="element-672" data-mp="2r1kqz3rwvmun" />

      {/* Tecnología y control documental */}
      <section className="bg-white py-20 md:py-28" data-id="element-673" data-mp="fl0r7r2oeuue">
        <Container data-id="element-674" data-mp="fjhay0h0sjsm">
          <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16" data-id="element-675" data-mp="amz8x8d2qzjz">
            <div data-id="element-676" data-mp="akwkutvhfghw">
              <Eyebrow data-id="element-677" data-mp="odw1iz703gr1">Tecnología aplicada</Eyebrow>
              <Heading as="h2" size="lg" className="mt-5" data-id="element-678" data-mp="odmb5ai1n7kt">
                Cada unidad habilitada.
                <br data-id="element-679" data-mp="kd2c21dwttw1" />
                Cada documento bajo control.
              </Heading>
              <p className="mt-6 text-lg leading-relaxed text-graphite-500" data-id="element-680" data-mp="3st5bjjuvcc9a">
                La plataforma documental permite consultar el estado de flota, transportista, chofer, camión y acoplado:
                documentos requeridos, pendientes y verificados, vencimientos, frecuencia y documentación invalidante.
              </p>
              <ul className="mt-8 grid gap-x-8 gap-y-2 sm:grid-cols-2" data-id="element-681" data-mp="qw7k4q1r4y3">
                {TECH_CAPABILITIES.slice(0, 6).map(cap => <li key={cap.title} className="text-sm text-graphite-500" data-id="element-682" data-mp="1c4hx3qztj8k5">
                    · {cap.title}
                  </li>)}
              </ul>
              <Link to="/tecnologia" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-700 transition-colors duration-150 hover:text-brand-600" data-id="element-683" data-mp="2cczredfb1r7y">
                Ver tecnología y control documental
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-684" data-mp="2mcgsehjm3gxl" />
              </Link>
            </div>
            <DocumentPlatform data-id="element-685" data-mp="2qawpxfdbk7p9" />
          </div>
        </Container>
      </section>

      {/* Modelo Rabemar */}
      <section className="bg-mist-50 py-20 md:py-28" data-id="element-686" data-mp="302kacwbgkoh8">
        <Container data-id="element-687" data-mp="303vhn6444ed3">
          <div className="max-w-3xl" data-id="element-688" data-mp="304nbabe5g20q">
            <Eyebrow data-id="element-689" data-mp="3526od7z428r3">Modelo Rabemar</Eyebrow>
            <Heading as="h2" size="lg" className="mt-5" data-id="element-690" data-mp="35140za0zyofx">
              Cómo tomamos una operación y la ponemos bajo control.
            </Heading>
          </div>
          <ModelSteps data-id="element-691" data-mp="3e1tnto8a54hi" />
          <div className="mt-14 border-t border-mist-200 pt-10" data-id="element-692" data-mp="3e34wa1co70ey">
            <p className="font-display text-2xl font-bold leading-tight tracking-tight text-graphite-900 md:text-3xl" data-id="element-693" data-mp="12fhskzz9h14w">
              No vendemos horas.
              <br data-id="element-694" data-mp="yc3dcbj5cosi" />
              Diseñamos operaciones que funcionan.
            </p>
          </div>
        </Container>
      </section>

      {/* Sustentabilidad + industrias */}
      <section className="bg-brand-900 py-20 md:py-28" data-id="element-695" data-mp="ogieundae6ni">
        <Container data-id="element-696" data-mp="1m2pls3x2cb31">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16" data-id="element-697" data-mp="1m38t6qp37h2t">
            <div data-id="element-698" data-mp="1h3w4s1iki1y4">
              <Eyebrow tone="dark" data-id="element-699" data-mp="1h3cn8lwtxfkm">Sustentabilidad</Eyebrow>
              <Heading as="h2" size="md" tone="dark" className="mt-5" data-id="element-700" data-mp="1h5z5bfhcjdh3">
                Medir. Reducir. Mejorar.
              </Heading>
              <p className="mt-5 text-base leading-relaxed text-mist-200" data-id="element-701" data-mp="17ekzkpysxhfi">
                Medición de huella de carbono, reportes mensuales de CO₂ y análisis de operaciones para identificar
                oportunidades concretas de reducción y modernización de flota.
              </p>
              <Link to="/soluciones/sustentabilidad" className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-accent-400 transition-colors duration-150 hover:text-accent-300" data-id="element-702" data-mp="3uakqmn63jchp">
                Ver programa de sustentabilidad
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-703" data-mp="3kiycahygoxaq" />
              </Link>
            </div>
            <div className="relative isolate min-h-[260px] overflow-hidden" data-id="element-704" data-mp="3amv2qr1a5wdr">
              <img src={IMAGES.sustainability} alt="Vista aérea de un centro de distribución con paneles solares en su techo y áreas verdes" className="absolute inset-0 h-full w-full object-cover" loading="lazy" data-id="element-705" data-mp="3aopmi20tfig9" />
            </div>
          </div>
        </Container>
      </section>

      <ProofSection data-id="element-706" data-mp="zvlemfvozhu1" />

      {/* FAQ */}
      <section className="bg-mist-50 py-20 md:py-28" data-id="element-707" data-mp="zspimx3en388">
        <Container data-id="element-708" data-mp="14slo70rr32pw">
          <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16" data-id="element-709" data-mp="14t4x5rzfagrs">
            <div data-id="element-710" data-mp="1eymub7kihglr">
              <Eyebrow data-id="element-711" data-mp="1ey3hg59pxiel">Preguntas frecuentes</Eyebrow>
              <Heading as="h2" size="md" className="mt-5" data-id="element-712" data-mp="19z0jf4s5ocg4">
                Lo que suelen preguntar antes de avanzar.
              </Heading>
            </div>
            <Faq items={FAQS} data-id="element-713" data-mp="1nrzy54k462mm" />
          </div>
        </Container>
      </section>

      <CtaBand title="Cuéntenos qué necesita resolver." text="Analizamos su operación actual y proponemos un alcance concreto: qué asume Rabemar, con qué procesos, qué indicadores se miden y cómo se controla el cumplimiento." primaryLabel="Solicitar diagnóstico" location="home-footer-cta" data-id="element-714" data-mp="1ixm0evhzbjes" />
    </>;
}